import type { TeamRole } from '../types/team.js';
import type { OrganizationRole } from '../types/organization.js';
import type { RbacAction, OrgRbacAction } from '../types/rbac.js';

/**
 * Role → allowed actions matrix (from PRD section 6.3).
 */
export const ROLE_PERMISSIONS: Record<TeamRole, readonly RbacAction[]> = {
  owner: [
    'team.manage_settings',
    'team.delete',
    'team.manage_members',
    'agent.create',
    'agent.edit',
    'agent.deploy',
    'agent.view',
    'agent.revoke',
    'agent.pause',
    'agent.impersonate',
    'agent.viewAuditLog',
    'template.manage',
    'audit_log.view',
    'host.create',
    'host.manage',
    'host.view',
    'integration.view',
    'integration.install',
    'integration.configure',
    'integration.manage_scopes',
    'integration.approve_requests',
    'project.view',
    'project.create',
    'project.edit',
    'workflow.author',
    'workflow.promote',
  ],
  admin: [
    'team.manage_members',
    'agent.create',
    'agent.edit',
    'agent.deploy',
    'agent.view',
    'agent.revoke',
    'agent.pause',
    'agent.impersonate',
    'agent.viewAuditLog',
    'template.manage',
    'audit_log.view',
    'host.create',
    'host.manage',
    'host.view',
    'integration.view',
    'integration.install',
    'integration.configure',
    'integration.manage_scopes',
    'integration.approve_requests',
    'project.view',
    'project.create',
    'project.edit',
    'workflow.author',
    'workflow.promote',
  ],
  member: [
    'agent.create',
    'agent.edit',
    'agent.deploy',
    'agent.view',
    'audit_log.view',
    'host.view',
    'integration.view',
    'integration.install',
    'integration.configure',
    'integration.manage_scopes',
    'project.view',
    'project.create',
    'project.edit',
    // Members can author curated workflow drafts but not promote them to
    // active — promotion (workflow.promote) is owner/admin only.
    'workflow.author',
  ],
  viewer: [
    'agent.view',
    'audit_log.view',
    'host.view',
    'integration.view',
    'project.view',
  ],
} as const;

const permissionSets = new Map<TeamRole, Set<RbacAction>>(
  (Object.entries(ROLE_PERMISSIONS) as [TeamRole, readonly RbacAction[]][]).map(
    ([role, actions]) => [role, new Set(actions)],
  ),
);

export function canPerform(role: TeamRole, action: RbacAction): boolean {
  const allowed = permissionSets.get(role);
  return allowed?.has(action) ?? false;
}

/**
 * Org Role → allowed org actions matrix.
 */
export const ORG_ROLE_PERMISSIONS: Record<OrganizationRole, readonly OrgRbacAction[]> = {
  owner: [
    'org.manage_settings',
    'org.delete',
    'org.manage_members',
    'org.manage_teams',
    'org.manage_guardrails',
    'org.manage_integrations',
    'org.view_audit_log',
  ],
  admin: [
    'org.manage_settings',
    'org.manage_members',
    'org.manage_teams',
    'org.manage_guardrails',
    'org.manage_integrations',
    'org.view_audit_log',
  ],
  member: [
    'org.manage_teams',
    'org.view_audit_log',
  ],
  viewer: [
    'org.view_audit_log',
  ],
} as const;

const orgPermissionSets = new Map<OrganizationRole, Set<OrgRbacAction>>(
  (Object.entries(ORG_ROLE_PERMISSIONS) as [OrganizationRole, readonly OrgRbacAction[]][]).map(
    ([role, actions]) => [role, new Set(actions)],
  ),
);

export function canPerformOrg(role: OrganizationRole, action: OrgRbacAction): boolean {
  const allowed = orgPermissionSets.get(role);
  return allowed?.has(action) ?? false;
}
